import { WModalButton } from "@/components/button/WToolButton.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { getFormattedMaintenance, startTimes, timeZones } from "@/modules/services/kubernetes/maintenanceSpec.tsx";
import { updateServicePropertiesMutation } from "@/modules/services/servicesQuery.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ManagedKubernetesService } from "@warrenio/api-spec/spec.oats.gen";
import { Controller, type SubmitHandler, useForm, useFormContext } from "react-hook-form";

export interface MaintenanceEditInputs {
    begin: string;
    timeZone: string;
    machineImageVersion: boolean;
    kubernetesVersion: boolean;
}

function MaintenanceEditModalContent({ item }: { item: ManagedKubernetesService }) {
    const { t } = useLingui();
    const mutation = useStandardMutation(updateServicePropertiesMutation);
    const { properties, uuid } = item;

    const defaultValues = {
        begin: properties?.spec?.maintenance.timeWindow.begin.slice(0, 6),
        timeZone: properties?.spec?.maintenance.timeWindow.begin.slice(
            6,
            properties.spec.maintenance.timeWindow.begin.length,
        ),
        machineImageVersion: properties?.spec?.maintenance.autoUpdate.machineImageVersion,
        kubernetesVersion: properties?.spec?.maintenance.autoUpdate.kubernetesVersion,
    };

    const form = useForm<MaintenanceEditInputs>({
        disabled: mutation.isPending,

        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues,
    });

    const onSubmit: SubmitHandler<MaintenanceEditInputs> = async (inputs) => {
        const { begin, timeZone, machineImageVersion, kubernetesVersion } = inputs;

        const maintenance = getFormattedMaintenance(begin, timeZone, machineImageVersion, kubernetesVersion);

        const provider = {
            workers: properties?.spec?.provider?.workers ?? [],
        };

        await mutation.mutateAsync({ uuid: uuid, body: { maintenance, provider } });
    };

    return (
        <WModalContent
            title={t`Edit maintenance`}
            label={t`Edit`}
            modalAction={async () => {
                await onSubmit(form.getValues());
            }}
        >
            <WHookForm form={form} onSubmit={onSubmit}>
                <MaintenanceFormContent />
            </WHookForm>
        </WModalContent>
    );
}

export function MaintenanceEditModal({ item }: { item: ManagedKubernetesService }) {
    const { t } = useLingui();
    return (
        <WModal button={<WModalButton label={t`Edit`} size="bar" variant="border" icon="jp-icon-edit" />}>
            <MaintenanceEditModalContent item={item} />
        </WModal>
    );
}

export function MaintenanceFormContent() {
    const { t } = useLingui();
    const { control } = useFormContext<MaintenanceEditInputs>();

    return (
        <>
            <FormField
                label={t`Maintenance`}
                description={t`Provider start of maintenance time window in which Provider may schedule automated cluster updates.`}
            >
                <div className="flex">
                    <WTextField headless block label={t`Start time`} onChange={undefined}>
                        <Controller
                            control={control}
                            name="begin"
                            render={({ field }) => (
                                <WSelect
                                    className="max-w-9rem"
                                    valueKey={field.value}
                                    getKey={(item) => item.id}
                                    items={startTimes}
                                    aria-label="Select status"
                                    getTextValue={(item) => item.title}
                                    onChange={(item) => field.onChange(item.id)}
                                >
                                    {({ title }) => title}
                                </WSelect>
                            )}
                        />
                    </WTextField>

                    <WTextField className="mt-0! ml-4" headless block label={t`Timezone`} onChange={undefined}>
                        <Controller
                            control={control}
                            name="timeZone"
                            render={({ field }) => (
                                <WSelect
                                    className="max-w-6.5rem"
                                    valueKey={field.value}
                                    getKey={(item) => item.id}
                                    items={timeZones}
                                    aria-label="Select status"
                                    getTextValue={(item) => item.title}
                                    onChange={(item) => field.onChange(item.id)}
                                >
                                    {({ title }) => title}
                                </WSelect>
                            )}
                        />
                    </WTextField>
                </div>
            </FormField>

            <FormField label={t`Auto update`} description={t`Automatic updates will not update preview versions.`}>
                <div className="pb-2">
                    <Controller
                        control={control}
                        name="machineImageVersion"
                        render={({ field }) => (
                            <WCheckbox isSelected={field.value} onChange={(value) => field.onChange(value)}>
                                <Trans>Operating system</Trans>
                            </WCheckbox>
                        )}
                    />
                </div>

                <Controller
                    control={control}
                    name="kubernetesVersion"
                    render={({ field }) => (
                        <WCheckbox isSelected={field.value} onChange={(value) => field.onChange(value)}>
                            <Trans>Kubernetes Patch Version</Trans>
                        </WCheckbox>
                    )}
                />
            </FormField>
        </>
    );
}
